<template>
  <div class="afdc-dropdown afdc-select-multiple-dropdown" v-on-clickaway="close">
    <button class="afdc-custom-select" type="button" v-on:click="toggle" v-bind:id="id" data-toggle="dropdown" aria-haspopup="true" v-bind:aria-expanded="show ? 'true' : 'false'">{{ selectedText }}</button>
    <div v-bind:class="{ 'afdc-dropdown-menu': true, 'afdc-show': show }" v-bind:aria-labelledby="id">
      <!-- eslint-disable-next-line vue/require-v-for-key -->
      <div v-for="group in groupedOptions">
        <div v-if="group.name" class="afdc-select-multiple-group-label">{{ group.name }}</div>
        <div v-for="option in group.options" v-bind:key="option.key">
          <div class="afdc-custom-control afdc-custom-checkbox">
            <input type="checkbox" class="afdc-custom-control-input" v-bind:value="option.key" v-model="inputValue" v-bind:id="optionIdPrefix + option.key">
            <label class="afdc-custom-control-label" v-bind:for="optionIdPrefix + option.key">{{ option.name }}</label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

// A component for treating multi-selects as a dropdown with checkboxes.
//
// Similar to https://github.com/davidstutz/bootstrap-multiselect or
// https://github.com/HemantNegi/jquery.sumoselect, but we'll do our own,
// simple implementation that's easier to get working with all of our required
// "afdc-" CSS class name prefixes (with our prefix requirements, it's hard to
// get the other plugins working since they assume normal, non-prefixed CSS
// class names).
//
// TODO: Better keyboard navigation support. You can tab through the
// checkboxes, but since this is supposed to act more like a menu, keyboard
// arrow support would probably be preferable.
// TODO: Better ARIA setup. Should this be role=menu?
// TODO: Better mobile handling. If we want to better support mobile devices,
// we might want to look into an implementation more like jquery.sumoselect
// which seems to get this most right. Sumoselect either turns the dropdown
// into more of a popup on small screen sizes, or it reverts back to a native
// <select multiple> input on mobile devices (and since mobile devices native
// implementation actually have decent <select multiple> usability, unlike
// desktop browsers, this would probably be preferable.
export default {
  mixins: [clickaway],

  props: {
    id: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: Array,
    },
  },

  data() {
    return {
      uniqueId: null,
      show: false,
    };
  },

  mounted() {
    // eslint-disable-next-line no-underscore-dangle
    this.uniqueId = this._uid;
  },

  computed: {
    inputValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },

    groupedOptions() {
      const groupedOptions = [];
      const defaultGroupOptions = [];
      const { options } = this;
      if (options) {
        options.forEach((option) => {
          if (option.options) {
            groupedOptions.push(option);
          } else {
            defaultGroupOptions.push(option);
          }
        });

        if (defaultGroupOptions.length > 0) {
          groupedOptions.push({
            options: defaultGroupOptions,
          });
        }
      }

      return groupedOptions;
    },

    flatOptions() {
      const flatOptions = [];
      const { options } = this;
      if (options) {
        options.forEach((option) => {
          if (option.options) {
            option.options.forEach((groupOption) => {
              flatOptions.push(groupOption);
            });
          } else {
            flatOptions.push(option);
          }
        });
      }

      return flatOptions;
    },

    optionNamesByKey() {
      const names = {};
      if (this.options) {
        this.flatOptions.forEach((option) => {
          names[option.key] = option.name;
        });
      }

      return names;
    },

    optionIdPrefix() {
      return `afdc-select-multiple-dropdown-option-${this.uniqueId}-`;
    },

    selectedText() {
      if (Array.isArray(this.value)) {
        const names = [];
        this.value.forEach((key) => {
          names.push(this.optionNamesByKey[key]);
        });

        const { length } = names;
        if (length > 3) {
          return this.$sprintf(this.$t('%s Selected'), this.$formatNumber(length));
        }
        return names.join(', ');
      }
      return '';
    },
  },

  methods: {
    toggle() {
      this.show = !this.show;
    },

    close() {
      this.show = false;
    },
  },
};
</script>

<style lang="scss">
.select-multiple-dropdown {
  .select-multiple-group-label {
    font-weight: bold;
  }

  .custom-select {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dropdown-menu {
    width: 100%;
    padding-left: 12px;
    padding-right: 12px;
    max-height: 180px;
    overflow-y: auto;
  }
}
</style>
